"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { FileText, LineChart, Target } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/ai-mock-interview";
const APP = "/interview";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "live-interview",
    name: "Live Interview",
    title: "A Real Conversation, Out Loud",
    description:
      "The AI interviewer speaks, listens and asks follow-ups based on what you say. It feels like the real call, so the real call feels familiar.",
    imageSrcUrl: `${IMG}/hero-live-interview.png`,
    primaryActionText: "Start a Mock Interview",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "interview-setup",
    name: "Any Round",
    title: "Practise the Round You Have Next",
    description:
      "Pick the role, the level and a Technical, Behavioral or Mixed format, then add your tech stack. Every session matches the interview in your calendar.",
    imageSrcUrl: `${IMG}/hero-interview-setup.png`,
    primaryActionText: "Start a Mock Interview",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "feedback",
    name: "Scored Feedback",
    title: "Know Exactly How You Did",
    description:
      "Get a score out of 100 and a breakdown across technical knowledge, problem solving, communication, cultural fit and confidence, each with a short note.",
    imageSrcUrl: `${IMG}/hero-feedback.png`,
    primaryActionText: "Start a Mock Interview",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "strengths-gaps",
    title: "Strengths and Gaps, in Writing",
    subtitle: "WHAT TO KEEP, WHAT TO FIX",
    description:
      "After each session you get the strengths worth repeating and the specific habits costing you points, with one clear thing to work on before next time.",
    benefits: [
      "Strengths with examples",
      "Areas to improve, ranked",
      "A final assessment",
      "One-click retake",
    ],
    primaryCTA: "Get My Feedback",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/strengths-gaps.png`,
    icon: <Target className="w-4 h-4" />,
    stats: "Feedback after every session",
  },
  {
    id: "transcript-review",
    title: "Every Answer, Transcribed",
    subtitle: "HEAR WHAT THEY HEARD",
    description:
      "Read the full transcript with timestamps. Spot where an answer wandered, where it landed, and how long you spoke before reaching the point.",
    benefits: [
      "Full timestamped transcript",
      "Strong moments highlighted",
      "Long answers flagged",
      "Search past sessions",
    ],
    primaryCTA: "Start Practising",
    primaryLink: APP,
    secondaryCTA: "Research the Company",
    secondaryLink: "/features/jobs-deep-research",
    imageSrcUrl: `${IMG}/transcript-review.png`,
    icon: <FileText className="w-4 h-4" />,
    stats: "Transcript saved",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "session-history",
    title: "Track Every Session",
    subtitle: "PRACTICE YOU CAN MEASURE",
    description:
      "Every interview is saved with its role, level, type and score. Reopen the feedback for any session and watch your scores climb as you practise.",
    benefits: [
      "Interview history in one place",
      "Score on every card",
      "Search by role or tech",
      "Feedback a click away",
    ],
    primaryCTA: "Start a Mock Interview",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/session-history.png`,
    icon: <LineChart className="w-4 h-4" />,
    stats: "Scores over time",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I knew the answers but rambled under pressure. Three sessions in, my communication score went from 58 to 79 and I stopped losing the thread.",
    name: "Neha V.",
    role: "Frontend Developer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The follow-up questions are what make it useful. It caught a vague claim in my first answer and pushed on it, exactly like my panel did a week later.",
    name: "Karan B.",
    role: "Sales Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "Reading my own transcript was uncomfortable and very useful. I could see where I took two minutes to reach the point.",
    name: "Ananya T.",
    role: "Data Scientist",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function InterviewAIPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Practise Out Loud.",
            titleHighlight: "Walk In Ready.",
            subtitle:
              "Run a realistic voice interview with an AI interviewer for your exact role and level. Get a full transcript, a score out of 100 and specific feedback on what to change before the real one.",
            ctaText: "Start a Mock Interview",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Practise Until It Sounds Natural.",
            sectionSubtitle:
              "Feedback that tells you exactly what to keep and what to change.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Calmer Interviews, Better Offers",
            subtitle: "From job seekers who rehearsed before the real thing",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About AI Mock Interview",
            subtitle: "How sessions work and what you get afterwards.",
            faqs,
            ctaTitle: "Ready for a practice round?",
            ctaText: "Start a Mock Interview",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
